import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Check, Users } from "lucide-react";

const formatEur = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " \u20ac";
};

function InvoiceButtonStyle(isSelected, isMatch) {
  if (isSelected) return "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300";
  if (isMatch) return "border-emerald-400 bg-emerald-50/50 hover:bg-emerald-50";
  return "border-slate-200 hover:bg-slate-50";
}

function InvoiceButtonIcon({ isSelected, isMatch }) {
  if (isSelected) return <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center shrink-0"><Check size={10} className="text-white" /></div>;
  if (isMatch) return <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><Check size={10} className="text-emerald-600" /></div>;
  return <div className="w-5 h-5 rounded-full border-2 border-slate-300 shrink-0" />;
}

function InvoiceRow({ inv, isSelected, isMatch, onSelect, showCompanyBadge }) {
  const remaining = inv.remaining ?? (inv.total_ttc - (inv.total_paid || 0));
  return (
    <button onClick={() => onSelect(inv)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md border text-left transition-all ${InvoiceButtonStyle(isSelected, isMatch)}`} data-testid={`assign-invoice-${inv.number || inv.id}`}>
      <InvoiceButtonIcon isSelected={isSelected} isMatch={isMatch} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium">{inv.number}</span>
          {showCompanyBadge && <Badge className="bg-indigo-100 text-indigo-700 text-[9px] px-1.5 py-0 font-semibold">{inv.client_name}</Badge>}
          <span className="text-xs text-slate-400">{inv.date}</span>
          {isMatch && !isSelected && <Badge className="bg-emerald-100 text-emerald-700 text-[9px] px-1 py-0">Correspondance</Badge>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-medium">{formatEur(inv.total_ttc)}</p>
        <p className="text-[10px] text-slate-400">reste {formatEur(remaining)}</p>
      </div>
    </button>
  );
}

export function AssignPaymentDialog({ assignDialog, setAssignDialog, onSelectInvoice, confirmAssign }) {
  if (!assignDialog) return null;
  const tx = assignDialog.tx;
  const currentClientInvs = (assignDialog.unpaidInvoices || []).filter(i => i.is_current_client);
  const otherClientInvs = (assignDialog.unpaidInvoices || []).filter(i => !i.is_current_client);

  return (
    <Dialog open={!!assignDialog} onOpenChange={(o) => !o && setAssignDialog(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="text-base">Affecter le règlement</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className={`rounded-md p-3 border ${tx.status === 'partiellement_affecte' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
            <p className={`text-xs uppercase tracking-wider mb-1 ${tx.status === 'partiellement_affecte' ? 'text-blue-600' : 'text-amber-600'}`}>Transaction bancaire</p>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-700">{tx.date}</span>
              <div className="text-right">
                {tx.status === 'partiellement_affecte' ? (
                  <>
                    <span className="text-xs text-slate-400 line-through mr-2">{formatEur(tx.original_amount)}</span>
                    <span className="text-base font-bold text-blue-700">reste {formatEur(assignDialog.txAvailable)}</span>
                  </>
                ) : (
                  <span className="text-base font-bold text-amber-700">{formatEur(tx.amount)}</span>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1 truncate" title={tx.label}>{tx.label}</p>
            {tx.assignments && tx.assignments.length > 0 && (
              <div className="mt-2 pt-2 border-t border-blue-200 space-y-1">
                <p className="text-[10px] text-slate-500 uppercase">Déjà affecté :</p>
                {tx.assignments.map((a, ai) => (
                  <div key={a.invoice_number || `assign-${ai}`} className="flex items-center gap-2 text-xs text-slate-600">
                    <Check size={10} className="text-emerald-500" />
                    <span className="font-mono">{a.invoice_number}</span>
                    {a.is_other_company && <Badge className="bg-indigo-100 text-indigo-700 text-[8px] px-1 py-0">{a.company}</Badge>}
                    <span className="ml-auto font-medium">{formatEur(a.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Factures impayees - sélectionnez une facture</p>
            {assignDialog.loading ? (
              <div className="flex justify-center py-4"><Loader2 className="animate-spin h-4 w-4 text-slate-400" /></div>
            ) : (assignDialog.unpaidInvoices || []).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Aucune facture impayee pour ce client</p>
            ) : (
              <div className="max-h-[350px] overflow-y-auto" data-testid="invoice-suggestions-list">
                {currentClientInvs.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {currentClientInvs.map(inv => {
                      const remaining = inv.remaining ?? (inv.total_ttc - (inv.total_paid || 0));
                      const isMatch = Math.abs(remaining - (assignDialog.txAvailable || tx.amount)) < 1;
                      return <InvoiceRow key={inv.id} inv={inv} isSelected={assignDialog.selectedInvoice === inv.id} isMatch={isMatch} onSelect={onSelectInvoice} showCompanyBadge={false} />;
                    })}
                  </div>
                )}
                {otherClientInvs.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 my-2 py-1.5 border-t border-b border-indigo-200 bg-indigo-50/50 px-2 rounded" data-testid="other-companies-separator">
                      <Users size={12} className="text-indigo-600" />
                      <span className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wider">Autres sociétés du contact</span>
                      <Badge className="bg-indigo-600 text-white text-[9px] px-1.5 py-0">{otherClientInvs.length}</Badge>
                    </div>
                    <div className="space-y-1">
                      {otherClientInvs.map(inv => {
                        const remaining = inv.remaining ?? (inv.total_ttc - (inv.total_paid || 0));
                        const isMatch = Math.abs(remaining - (assignDialog.txAvailable || tx.amount)) < 1;
                        return <InvoiceRow key={inv.id} inv={inv} isSelected={assignDialog.selectedInvoice === inv.id} isMatch={isMatch} onSelect={onSelectInvoice} showCompanyBadge={true} />;
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {assignDialog.selectedInvoice && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="assign-amount-section">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Montant a affecter</p>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <input type="number" step="0.01" min="0.01" max={assignDialog.txAvailable} value={assignDialog.assignAmount} onChange={e => setAssignDialog(prev => ({ ...prev, assignAmount: e.target.value }))} className="w-full h-9 px-3 pr-8 text-sm font-medium border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-300 outline-none" data-testid="assign-amount-input" />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">EUR</span>
                </div>
                <div className="text-xs text-slate-400 shrink-0">/ {formatEur(assignDialog.txAvailable)}</div>
              </div>
              {parseFloat(assignDialog.assignAmount) < assignDialog.txAvailable && parseFloat(assignDialog.assignAmount) > 0 && (
                <p className="text-[11px] text-indigo-600 mt-1.5">Affectation partielle : {formatEur(assignDialog.txAvailable - parseFloat(assignDialog.assignAmount))} resteront non affectes</p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setAssignDialog(null)} data-testid="assign-cancel-btn">Annuler</Button>
            <Button size="sm" disabled={!assignDialog.selectedInvoice || !parseFloat(assignDialog.assignAmount)} onClick={confirmAssign} data-testid="assign-confirm-btn">
              <Check size={14} className="mr-1" />Affecter {assignDialog.assignAmount ? formatEur(parseFloat(assignDialog.assignAmount)) : ""}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
